import {
  Center,
  ComponentMultiStyleConfig,
  Icon,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
  useMultiStyleConfig,
  useOutsideClick,
} from "@chakra-ui/react";
import React, { memo, ReactElement, useCallback, useMemo, useRef } from "react";
import { ComponentStyleProps } from "../../../../../lib/styles/props/component-style-props";
import { FaRegCommentDots } from "react-icons/fa";
import { CMBalloonEditRequest } from "../../popup";
import { hasValue } from "../../../../../lib/util/common-util";

export type CMButtonFormCommentProps = {
  icon?: ReactElement;
  size?: "sm" | "md" | "lg";
  ariaLabel?: string;

  /**
   * メインコメント・返信コメントの編集ボタンのラベル・Ariaラベル。
   * 編集可能とする場合、設定を強く推奨する。
   */
  editButtonLabel?: string;
  /**
   * テキストエリアの最大文字数。
   * 指定しなければ無制限。
   */
  maxLength?: number;

  /**
   * 編集モードの表示項目に使用
   */
  commentInput: {
    main: {
      placeholder?: string;
      finishButtonLabel: string;
      cancelButtonLabel: string;
      deleteButtonLabel: string;
    };
    reply: {
      placeholder?: string;
      finishButtonLabel: string;
      cancelButtonLabel: string;
      deleteButtonLabel: string;
    };
  };

  /**
   * 削除確認時の表示項目に使用
   */
  deleteConfirmationModal: {
    title: string;
    message?: string;
    deleteButtonLabel: string;
    cancelButtonLabel: string;
  };

  mainComment?: {
    userName: string;
    date: Date;
    comment: string;
  };
  replyComment?: {
    userName: string;
    date: Date;
    comment: string;
  };

  /**
   * 開いたときのコールバック。
   */
  onOpen?: () => void;
  /**
   * 閉じたときのコールバック。
   */
  onClose?: () => void;

  /**
   * メインコメントが保存・更新されたときのコールバック。
   * この関数が存在すれば、メインコメント編集可能とする。
   *
   * nullに更新する場合、削除を表す。
   */
  onSaveMain?: (value: string | null) => void;

  /**
   * 返信コメントが保存・更新されたときのコールバック。
   * この関数が存在すれば、返信コメント編集可能とする。
   *
   * nullに更新する場合、削除を表す。
   */
  onSaveReply?: (value: string | null) => void;

  /**
   * HTML属性のID。
   */
  id?: string;

  usePortal?: boolean;
} & ComponentStyleProps;

/**
 * コメントボタンを表示するコンポーネント
 */
export const CMButtonFormComment = memo(function CMButtonFormComment({
  icon = <Icon as={FaRegCommentDots} />,
  size = "md",
  ariaLabel = "Comment",

  editButtonLabel,
  maxLength,

  commentInput,

  deleteConfirmationModal,

  mainComment,
  replyComment,

  onOpen,
  onClose,

  onSaveMain,
  onSaveReply,

  id,

  usePortal = false,

  sx,
  ...rest
}: CMButtonFormCommentProps) {
  const styles = useMultiStyleConfig("CMButtonFormCommentStyles", { size });

  const hasComment = useMemo(
    () => hasValue(mainComment) || hasValue(replyComment),
    [mainComment, replyComment],
  );

  const {
    onOpen: onOpenPopup,
    onClose: onClosePopup,
    isOpen: isOpenPopup,
  } = useDisclosure();

  // 削除確認モーダルの制御に使用する。
  const deleteConfirmationModalSettings = useDisclosure();

  const iconButtonStyles = useMemo(
    () => ({
      ...styles.iconButton,
      ...(isOpenPopup ? styles.iconButtonActive : undefined),
      ...(hasComment ? styles.iconButtonHasComment : undefined),
      ...(hasComment && isOpenPopup
        ? styles.iconButtonHasCommentActive
        : undefined),
    }),
    [styles, hasComment, isOpenPopup],
  );

  const handleOpenPopup = useCallback(() => {
    if (isOpenPopup) return;
    onOpenPopup();
    onOpen?.();
  }, [isOpenPopup, onOpenPopup, onOpen]);
  const handleClosePopup = useCallback(() => {
    if (!isOpenPopup) return;
    onClosePopup();
    onClose?.();
  }, [isOpenPopup, onClosePopup, onClose]);

  const ref = useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      if (!deleteConfirmationModalSettings.isOpen) {
        handleClosePopup();
      }
    },
  });

  const popoverContent = useMemo(() => {
    return (
      <PopoverContent className={"PopoverContent"} border={"none"} ref={ref}>
        <CMBalloonEditRequest
          className={"CMBalloonEditRequest"}
          editButtonLabel={editButtonLabel}
          maxLength={maxLength}
          commentInput={commentInput}
          deleteConfirmationModal={deleteConfirmationModal}
          mainComment={mainComment}
          replyComment={replyComment}
          deleteConfirmationModalSettings={deleteConfirmationModalSettings}
          onSaveMain={onSaveMain}
          onSaveReply={onSaveReply}
          sx={styles.popup}
        />
      </PopoverContent>
    );
  }, [
    commentInput,
    deleteConfirmationModal,
    deleteConfirmationModalSettings,
    editButtonLabel,
    mainComment,
    maxLength,
    onSaveMain,
    onSaveReply,
    replyComment,
    styles.popup,
  ]);

  return (
    <Center id={id} sx={styles.container}>
      <Popover
        placement={"bottom-start"}
        isOpen={isOpenPopup}
        onOpen={handleOpenPopup}
        onClose={handleClosePopup}
      >
        <PopoverTrigger>
          <IconButton
            aria-label={ariaLabel}
            variant="ghost"
            size={size}
            icon={icon}
            sx={{ ...iconButtonStyles, ...sx }}
            {...rest}
          />
        </PopoverTrigger>
        {usePortal ? <Portal>{popoverContent}</Portal> : popoverContent}
      </Popover>
    </Center>
  );
});

const colors = {
  default: {
    bgColor: "transparent",
    color: "gray.400",
  },
  defaultActive: {
    bgColor: "gray.100",
    color: "gray.600",
  },
  hasComment: {
    bgColor: "orange.500",
    color: "white",
  },
  hasCommentActive: {
    bgColor: "orange.400",
    color: "white",
  },
};

export const CMButtonFormCommentStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "iconButton",
    "iconButtonHasComment",
    "iconButtonActive",
    "balloon",
    "popup",
  ],
  baseStyle: {
    container: {
      position: "relative",
    },
    iconButton: {
      borderRadius: "md",

      ...colors.default,

      _hover: {
        ...colors.defaultActive,
      },
    },
    iconButtonActive: {
      ...colors.defaultActive,
    },
    iconButtonHasComment: {
      ...colors.hasComment,

      _hover: {
        ...colors.hasCommentActive,
      },
    },
    iconButtonHasCommentActive: {
      ...colors.hasCommentActive,
    },
    balloon: {
      position: "absolute",

      top: "100%",
      left: "10px",

      w: "max-content",

      zIndex: 10,
    },
    popup: {
      position: "absolute",

      top: "100%",
      left: "10px",

      w: "max-content",

      zIndex: 10,
    },
  },
  sizes: {
    sm: {
      container: { w: "20px", h: "20px" },
      iconButton: { minW: "20px", w: "20px", h: "20px" },
    },
    md: {
      container: { w: "24px", h: "24px" },
      iconButton: { minW: "24px", w: "24px", h: "24px" },
    },
    lg: {
      container: { w: "28px", h: "28px" },
      iconButton: { minW: "28px", w: "28px", h: "28px" },
    },
  },
};
